import { createReducer, on } from '@ngrx/store';
import { AuthActions, AuthApiActions, AuthEffectActions } from './auth.actions';
import { AuthState, deriveKind, emptyAuthState, toGuestPermissionMap } from './auth.state';

/**
 * The save methods used to guard every write with `if (!v || v == 'undefined') return`.
 * Those guards live here now, so state and localStorage cannot drift apart: the
 * persister mirrors post-reduction state, so anything the reducer rejects is
 * never written either.
 */
function isWritable(value: any): boolean {
  return !!value && value !== 'undefined';
}

export const authReducer = createReducer(
  emptyAuthState,

  on(AuthActions.tokenSet, (state, { token }): AuthState =>
    isWritable(token) ? { ...state, token } : state),

  on(AuthActions.userSet, (state, { user }): AuthState =>
    isWritable(user) ? { ...state, user, kind: deriveKind(user) } : state),

  on(AuthActions.organisationSet, (state, { organisation }): AuthState =>
    isWritable(organisation) ? { ...state, organisation } : state),

  on(AuthActions.organisationCleared, (state): AuthState =>
    ({ ...state, organisation: null })),

  on(AuthActions.permissionsSet, (state, { permissions }): AuthState =>
    isWritable(permissions) ? { ...state, permissions, permissionsStatus: 'loaded' } : state),

  on(AuthActions.permissionsCleared, (state): AuthState =>
    ({ ...state, permissions: {}, permissionsStatus: 'idle' })),

  on(AuthActions.guestPermissionsSet, (state, { permissions }): AuthState =>
    !permissions || permissions.length === 0
      ? state
      : { ...state, permissions: toGuestPermissionMap(permissions), permissionsStatus: 'loaded' }),

  on(AuthActions.collabOrgSet, (state, { orgId }): AuthState =>
    isWritable(orgId) ? { ...state, collabOrgId: orgId } : state),

  on(AuthActions.collabOrgCleared, (state): AuthState =>
    ({ ...state, collabOrgId: null })),

  on(AuthActions.currentProjectSet, (state, { project }): AuthState =>
    isWritable(project) ? { ...state, currentProject: project } : state),

  // Resets everything, which is what fixes two long-standing leaks: logout()
  // removed 'current-project' while the key written was 'onepass-current-project',
  // and it never cleared 'collab-org' at all, so a stale collaboration header rode
  // along into the next sign-in on that browser.
  on(AuthActions.signedOut, (): AuthState => emptyAuthState),

  on(AuthApiActions.signInSucceeded, (state, { token }): AuthState =>
    ({ ...state, token })),

  on(AuthApiActions.profileLoaded, (state, { user, organisation }): AuthState =>
    ({ ...state, user, organisation, kind: deriveKind(user) })),

  on(AuthApiActions.guestSignInSucceeded, (state, a): AuthState => ({
    ...state,
    kind: 'guest',
    token: a.token,
    user: a.user,
    organisation: a.organisation,
    permissions: toGuestPermissionMap(a.permissions),
    permissionsStatus: 'loaded',
  })),

  // Mirrors the old getPermissions, which cleared permissions to a bare ARRAY
  // before fetching. checkPermission has an Array.isArray branch that depends on
  // this, so the transitional shape is preserved rather than tidied to {}.
  on(AuthEffectActions.permissionsLoadRequested, (state): AuthState =>
    ({ ...state, permissions: [], permissionsStatus: 'loading' })),

  on(AuthApiActions.permissionsLoaded, (state, { permissions }): AuthState =>
    ({ ...state, permissions, permissionsStatus: 'loaded' })),

  on(AuthApiActions.permissionsLoadFailed, (state): AuthState =>
    ({ ...state, permissions: [], permissionsStatus: 'error' })),

  on(AuthApiActions.organisationSwitchSucceeded, (state, { token, organisation }): AuthState =>
    ({ ...state, token, organisation })),
);
